import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck } from "lucide-react";
import { CarrierFormData } from "./CarrierSignup";

interface Props {
  handleInputChange: (field: keyof CarrierFormData, value: any) => void;
  formData: CarrierFormData;
  handleArrayToggle: (field: keyof CarrierFormData, value: string) => void;
}

const vehicleTypes = [
  "Dry Van",
  "Refrigerated",
  "Flatbed",
  "Step Deck",
  "Lowboy",
  "Tanker",
  "Box Truck",
  "Straight Truck",
  "Car Carrier",
  "Heavy Haul",
];

export function FleetInformation({
  handleInputChange,
  formData,
  handleArrayToggle,
}: Props) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Fleet Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Fleet Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="numberOfDrivers">Number of Drivers *</Label>
              <Input
                id="numberOfDrivers"
                type="number"
                value={formData.numberOfDrivers}
                onChange={(e) =>
                  handleInputChange(
                    "numberOfDrivers",
                    Number.parseInt(e.target.value) || 0
                  )
                }
                placeholder="0"
              />
            </div>
            <div>
              <Label htmlFor="numberOfVehicles">Number of Vehicles *</Label>
              <Input
                id="numberOfVehicles"
                type="number"
                value={formData.numberOfVehicles}
                onChange={(e) =>
                  handleInputChange(
                    "numberOfVehicles",
                    Number.parseInt(e.target.value) || 0
                  )
                }
                placeholder="0"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="operatingRadius">Operating Radius *</Label>
            <select
              id="operatingRadius"
              value={formData.operatingRadius}
              onChange={(e) =>
                handleInputChange("operatingRadius", e.target.value)
              }
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
            >
              <option value="">Select Operating Radius</option>
              <option value="local">Local (within 100 miles)</option>
              <option value="regional">Regional (100-500 miles)</option>
              <option value="long_haul">Long Haul (500+ miles)</option>
              <option value="nationwide">Nationwide</option>
            </select>
          </div>
          <div>
            <Label>Vehicle Types *</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {vehicleTypes.map((type) => (
                <label key={type} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.vehicleTypes.includes(type)}
                    onChange={() => handleArrayToggle("vehicleTypes", type)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">{type}</span>
                </label>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Limits */}
      <Card>
        <CardHeader>
          <CardTitle>Vehicle Limits & Specifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="maxWeight">Maximum Weight Capacity *</Label>
            <Input
              id="maxWeight"
              value={formData.maxWeight}
              onChange={(e) => handleInputChange("maxWeight", e.target.value)}
              placeholder="80,000 lbs"
            />
          </div>
          <div>
            <Label htmlFor="maxLength">Maximum Length *</Label>
            <Input
              id="maxLength"
              value={formData.maxLength}
              onChange={(e) => handleInputChange("maxLength", e.target.value)}
              placeholder="53 feet"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="maxWidth">Maximum Width *</Label>
              <Input
                id="maxWidth"
                value={formData.maxWidth}
                onChange={(e) => handleInputChange("maxWidth", e.target.value)}
                placeholder="8.5 feet"
              />
            </div>
            <div>
              <Label htmlFor="maxHeight">Maximum Height *</Label>
              <Input
                id="maxHeight"
                value={formData.maxHeight}
                onChange={(e) => handleInputChange("maxHeight", e.target.value)}
                placeholder="13.6 feet"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
